import { Capacitor } from '@capacitor/core';
import Environment from '../../../helpers/environment';
import { getTokenExpirationDate } from '../../../helpers/tokenHelpers';

const apiUrl = import.meta.env.VITE_API_BASEURL;
const isUnifiedLoginDisabled = import.meta.env.VITE_DISABLE_UNIFIED_LOGIN === 'true';

const TOKEN_KEY = 'access_token';

export function handleEmailLogin(email, password, environmentUrl) {
  return fetch(`${environmentUrl}/users/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });
}

export function handlePhoneLogin(phone, environmentUrl) {
  return fetch(`${environmentUrl}/otp/send-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phone }),
  });
}

export function handleOtpVerify(code, phone, environmentUrl) {
  return fetch(`${environmentUrl}/otp/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code, phone }),
  });
}

export function requestAutoLogin(token) {
  return fetch(`${apiUrl}/users/auto-login`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

export function sendPasswordReset(email, environmentUrl) {
  return fetch(`${environmentUrl}/users/send-password-reset-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });
}

export const handleLoggingIn = async ({
  phone,
  email,
  password,
  environmentUrl,
  afterLogin,
  setErrorMessage,
}) => {
  const response = phone
    ? await handlePhoneLogin(phone, environmentUrl)
    : await handleEmailLogin(email, password, environmentUrl);

  if (!response.ok) {
    if (response.status === 503) {
      setErrorMessage('Service is currently unavailable. Please try again later.');
    }
    const errorMessage = phone ? 'Invalid phone number' : 'Invalid email or password';
    setErrorMessage(errorMessage);

    return null;
  }

  const loginResponse = await response.json();
  const loginObject = !phone ? loginResponse : { ...loginResponse, phone };

  return afterLogin && (afterLogin(loginObject));
};

function computeDomain(hostname) {
  const parts = hostname.split('.');
  const len = parts.length;

  // Check for .backrs.com (e.g., app.backrs.com)
  if (len >= 2 && parts[len - 2] === 'backrs' && parts[len - 1] === 'com') {
    return '.backrs.com';
  }

  // Check for *.backrs-dev.com (e.g., dev.backrs-dev.com, sub.stg.backrs-dev.com)
  if (len >= 3 && parts[len - 2] === 'backrs-dev' && parts[len - 1] === 'com') {
    const subDomain = parts[len - 3];

    // Ensure the subdomain is one of the known environments (dev, staging, stg, prd)
    // This prevents matching something like 'random.backrs-dev.com'
    if (['dev', 'staging', 'stg', 'prd'].includes(subDomain)) {
      return `.${subDomain}.backrs-dev.com`;
    }
  }

  // Default for localhost or other unknown hostnames
  return '.app.localhost';
}

export const setCookie = (token) => {
  // can't consistently handle cookies in native app, so making exception
  const isNative = Capacitor.isNativePlatform();
  if (isNative) {
    return;
  }

  const expirationDate = getTokenExpirationDate(token);
  const secureTag = Environment.isDevMode ? '' : '; secure';
  // eslint-disable-next-line max-len
  document.cookie = `token=${encodeURIComponent(token)}; domain=${computeDomain(window.location.hostname)}; path=/; expires=${expirationDate.toUTCString()}${secureTag}`;
};

export const getCookie = () => {
  // can't consistently handle cookies in native app, so making exception
  const isNative = Capacitor.isNativePlatform();
  if (isNative) {
    return null;
  }

  const cookie = document.cookie.split('; ').find((row) => row.startsWith('token='));

  if (!cookie) {
    return null;
  }

  const value = cookie.split('=')[1];

  return decodeURIComponent(value);
};

const tokenTypeMap = {
  prosper: 'backrs_prosper_token',
  backrs: 'backrs',
};

export const decodeToken = (tok) => {
  const base64Url = tok.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window.atob(base64)
      .split('')
      .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const getAccessToken = () => {
  const accessToken = localStorage.getItem(TOKEN_KEY);

  if (!accessToken) {
    return null;
  }

  return accessToken;
};

export const getAccessTokenOrCookie = () => {
  // can't consistently handle cookies in native app, so making exception
  const isNative = Capacitor.isNativePlatform();
  const cookie = getCookie();

  if (!cookie && !isNative && !isUnifiedLoginDisabled) {
    return null;
  }

  const token = getAccessToken() || cookie;

  try {
    decodeToken(token);
    return token;
  } catch (e) {
    return null;
  }
};

export const setAccessToken = (tokenType, token) => {
  localStorage.setItem(tokenTypeMap[tokenType], token);
};

export const deleteAccessToken = (tokenType) => {
  localStorage.removeItem(tokenTypeMap[tokenType]);
};

export const removeCookie = () => {
  document.cookie = `token=; path=/; domain=${computeDomain(window.location.hostname)}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};
